import { ArrowRight, Ban, Rocket, Route } from "lucide-react";
import type { ReactNode } from "react";
import InfoNote from "../components/InfoNote";

const UNATTENDED_APPROVAL_NOTE =
  "Per OpenAI's own Agents SDK docs (developers.openai.com): a pending tool approval simply waits for \"your application\" to resolve it — there's no built-in timeout, escalation, or auto-deny if nobody ever does.";

type CompareCard = {
  icon: ReactNode;
  headline: string;
  body: ReactNode;
};

const COMPARE_CARDS: CompareCard[] = [
  {
    icon: <Rocket className="h-5 w-5 text-primary" />,
    headline: "Fail-closed by default, not bolted on.",
    body: (
      <>
        A pending tool approval in most agent SDKs just waits for a human to show up — no built-in
        timeout, escalation, or auto-deny if nobody does.
        <InfoNote text={UNATTENDED_APPROVAL_NOTE} /> Anter fails closed automatically: an unattended
        or scheduled run auto-denies anything that needs a human's OK.
      </>
    ),
  },
  {
    icon: <Route className="h-5 w-5 text-primary" />,
    headline: "Routing you can prove, not routing you hope for.",
    body: "A 'manager agent' deciding who goes next is still just an LLM guessing. Anter validates every transfer and delegation against a write-time allowlist and a runtime existence check — and won't let your front-door agent fabricate an answer instead of routing.",
  },
  {
    icon: <Ban className="h-5 w-5 text-primary" />,
    headline: "Bounded, not just built.",
    body: "Agent-calls-agent chains are a real cost and latency risk — deep chains, accidental cycles, unbounded recursion. Anter enforces an exact cycle guard, a hop-depth ceiling, and step-budget decay on every cross-agent dispatch.",
  },
];

export default function WhyNotThem() {
  return (
    <section className="py-20">
      <div className="mb-12">
        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-primary-text">
          Why not the alternative
        </p>
        <h2 className="max-w-2xl text-3xl font-extrabold tracking-tight text-text sm:text-4xl">
          Anthropic and OpenAI will sell you a model with an agent bolted on.
          <br />
          We built the agent.
        </h2>
        <p className="mt-4 max-w-xl text-base leading-relaxed text-text-muted">
          The model providers ship multi-agent primitives — coordinators, handoffs, approval hooks.
          Whether they're validated, audited, and safe to leave unattended is left for you to build.
          The agent frameworks hand routing decisions to the LLM and hope it holds. Anter's
          orchestrator validates every handoff twice, persists it atomically, and bounds runaway
          agent-to-agent chains by design.
        </p>
      </div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {COMPARE_CARDS.map((card) => (
          <div key={card.headline} className="glass-panel group rounded-2xl p-6">
            <div className="mb-4 flex h-9 w-9 items-center justify-center rounded-lg border border-primary-border bg-primary-dim">
              {card.icon}
            </div>
            <h3 className="mb-2 text-base font-bold text-text">{card.headline}</h3>
            <p className="text-sm leading-relaxed text-text-muted">{card.body}</p>
          </div>
        ))}
      </div>

      <div className="mt-8">
        <a
          href="/why-anter"
          className="inline-flex items-center gap-2 text-sm font-semibold text-primary-text transition hover:opacity-80"
        >
          See the full comparison vs. Claude, OpenAI, CrewAI &amp; Base44
          <ArrowRight className="h-3.5 w-3.5" />
        </a>
      </div>
    </section>
  );
}
